// ────────────────────────────────────────────────────────────────
//  userAlertContext.js – central state provider for IncidentAlert
//  (SchoolShield-aligned: /authenticateSession + /authenticate-mfa-session)
// ────────────────────────────────────────────────────────────────
import React, { createContext, useEffect, useRef, useState } from 'react';
import axios, { logger } from './components/axiosAlertConfig';
import { getCurrentIncidentID } from './utils/incidentDeepLink';

export { logger };
export const UserContext = createContext(null);

function safeParseJSON(str) {
  try { return JSON.parse(str); } catch { return null; }
}

const mask = (v) => {
  const s = String(v ?? '');
  if (!s) return '(none)';
  if (s.length <= 10) return s;
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
};

const summarizeUser = (u) => {
  if (!u) return null;
  return {
    isAuthenticated: u.isAuthenticated,
    isMfaAuthenticated: u.isMfaAuthenticated,
    accountID: u.accountID || u.iAccountID,
    userID: u.userID || u.iUserID,
    role: u.userRole,
    hasSession_idField: !!u.session_id,
  };
};

function normalizeUserRow(row = {}) {
  const accountID =
    row.accountID ??
    row.iAccountID ??
    row.iaccountid ??
    row.iAccountId ??
    '';

  const userID =
    row.userID ??
    row.iUserID ??
    row.iuserid ??
    row.iUserId ??
    '';

  const firstName =
    row.firstName ??
    row.firstname ??
    row.FirstName ??
    row.userFirstName ??
    row.UserFirstName ??
    '';

  const lastName =
    row.lastName ??
    row.lastname ??
    row.LastName ??
    row.userLastName ??
    row.UserLastName ??
    '';

  const userMobileNumber =
    row.userMobileNumber ??
    row.UserMobileNumber ??
    row.mobileNumber ??
    row.MobileNumber ??
    row.mobile ??
    row.phone ??
    row.Phone ??
    '';

  return {
    ...row,
    accountID: accountID || row.accountID || '',
    iAccountID: accountID || row.iAccountID || '',
    userID: userID || row.userID || '',
    iUserID: userID || row.iUserID || '',
    firstName,
    lastName,
    userMobileNumber,
  };
}

const isCanceled = (err) =>
  err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';

export function UserProvider({ children }) {
  /* ───────────── AUTH / SESSION ───────────── */

  const [user, setUser] = useState(() => {
    const cached = safeParseJSON(sessionStorage.getItem('userInfo'));
    return cached ? { ...cached, isAuthenticated: false, isMfaAuthenticated: null } : null;
  });

  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem('sessionId') || '');
  const [isLoading, setIsLoading] = useState(true);
  const [bootComplete, setBootComplete] = useState(false);
  const [didCookieHydrate, setDidCookieHydrate] = useState(false);

  const [iAccountID, setIAccountID] = useState(() =>
    sessionStorage.getItem('iAccountID') ||
    safeParseJSON(sessionStorage.getItem('userInfo'))?.iAccountID ||
    safeParseJSON(sessionStorage.getItem('userInfo'))?.accountID ||
    ''
  );

  const [isTestIncident, setIsTestIncident] = useState(() => {
    if (localStorage.getItem('isTestIncidentOverride') === '1') return true;
    return localStorage.getItem('isTestIncident') === '1';
  });

  /* ───────────── LOCATION (restored) ───────────── */
  const [iBuildingID, setIBuildingID] = useState(localStorage.getItem('iBuildingID') || '');
  const [buildingName, setBuildingName] = useState(localStorage.getItem('buildingName') || '');
  const [address, setAddress] = useState(localStorage.getItem('address') || '');
  const [city, setCity] = useState(localStorage.getItem('city') || '');
  const [iState, setIState] = useState(localStorage.getItem('istate') || '');
  const [zipCode, setZipCode] = useState(localStorage.getItem('zipCode') || '');
  const [latitude, setLatitude] = useState(localStorage.getItem('latitude') || '');
  const [longitude, setLongitude] = useState(localStorage.getItem('longitude') || '');
  const [hazmat, setHazmat] = useState(localStorage.getItem('hazmat') || '');
  const [accountName, setAccountName] = useState(localStorage.getItem('accountName') || '');
  const [iPath, setIpath] = useState(localStorage.getItem('ipath') || '');

  /* live ref so event handler doesn't use stale state */
  const authedRef = useRef(false);
  useEffect(() => { authedRef.current = !!user?.isAuthenticated; }, [user?.isAuthenticated]);

  /* prevent duplicate SessionIdAuth calls */
  const authInFlightRef = useRef(false);

  /* MFA check: run once per session */
  const mfaCheckedKeyRef = useRef('');

  /* log initial boot snapshot once */
  useEffect(() => {
    logger.info('INIT state', {
      sessionId_state: mask(sessionId),
      sessionId_storage: mask(sessionStorage.getItem('sessionId')),
      cachedUserInfo: summarizeUser(safeParseJSON(sessionStorage.getItem('userInfo'))),
      didCookieHydrate,
      bootComplete,
      isLoading,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* listen for fresh sessionIds broadcast by LoginNew.jsx */
  useEffect(() => {
    const h = (e) => {
      const sid = e.detail?.sessionId;
      logger.info('EVENT newSessionId', { sid: mask(sid), raw: sid });

      if (!sid) return;

      sessionStorage.setItem('sessionId', sid);
      setSessionId(sid);

      if (!sessionStorage.getItem('sessionCreationTime')) {
        sessionStorage.setItem('sessionCreationTime', new Date().toISOString());
      }

      // If cookie SSO already authenticated us, DO NOT force loading
      const cached = safeParseJSON(sessionStorage.getItem('userInfo'));
      const alreadyAuthed = !!(cached?.isAuthenticated || authedRef.current);

      if (alreadyAuthed) {
        setIsLoading(false);
        setBootComplete(true);
        logger.info('EVENT newSessionId while already authed -> keep UI ready', {});
      } else {
        setIsLoading(true);
        setBootComplete(false);
        logger.info('EVENT newSessionId -> start SessionIdAuth', {});
      }
    };

    window.addEventListener('newSessionId', h);
    return () => window.removeEventListener('newSessionId', h);
  }, []);

  /* persist isTestIncident */
  useEffect(() => {
    const v = isTestIncident ? '1' : '0';
    sessionStorage.setItem('isTestIncident', v);
    localStorage.setItem('isTestIncident', v);
  }, [isTestIncident]);

  /* ──────────────────────────────────────────────
     0) Cookie bootstrap (SSO behavior)
     ────────────────────────────────────────────── */
  useEffect(() => {
    if (didCookieHydrate) return;
    if (user?.isAuthenticated) { setDidCookieHydrate(true); return; }
    if (sessionId) { setDidCookieHydrate(true); return; } // sessionId flow will handle it

    logger.info('CookieHydrate effect check', {
      didCookieHydrate,
      user_isAuthed: !!user?.isAuthenticated,
      sessionId_state: mask(sessionId),
      sessionId_storage: mask(sessionStorage.getItem('sessionId')),
    });

    (async () => {
      setIsLoading(true);

      try {
        logger.info('CookieHydrate calling /authenticateSession', { withCredentials: true });

        const { data } = await axios.get('/authenticateSession', { withCredentials: true });

        const row = Array.isArray(data) ? data[0] : null;
        if (!row) return;

        const u = normalizeUserRow(row);

        const sid = u.session_id || row.session_id;
        if (sid) {
          logger.info('CookieHydrate storing sessionId', { sid: mask(sid) });
          sessionStorage.setItem('sessionId', sid);
          if (!sessionStorage.getItem('sessionCreationTime')) {
            sessionStorage.setItem('sessionCreationTime', new Date().toISOString());
          }
          setSessionId(sid);
        }

        const merged = {
          ...(user || {}),
          ...u,
          isAuthenticated: true,
          isMfaAuthenticated: null,
        };

        setUser(merged);
        sessionStorage.setItem('userInfo', JSON.stringify(merged));
        logger.info('CookieHydrate setUser merged', { merged: summarizeUser(merged) });

        const acc = merged.iAccountID || merged.accountID;
        if (acc) {
          setIAccountID(acc);
          sessionStorage.setItem('iAccountID', acc);
        }
      } catch (err) {
        logger.info('CookieHydrate FAILED', {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
        });
      } finally {
        setDidCookieHydrate(true);
        setIsLoading(false);
        setBootComplete(true);
        logger.info('CookieHydrate FINALLY', { didCookieHydrate: true, bootComplete: true, isLoading: false });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didCookieHydrate, sessionId, user?.isAuthenticated]);

  /* ──────────────────────────────────────────────
     1) Authenticate existing SESSIONID
     ✅ FIX: dependencies DO NOT include isLoading/bootComplete
     (prevents abort/cancel loop that kicked you to /login on refresh)
     ────────────────────────────────────────────── */
  useEffect(() => {
    logger.info('SessionIdAuth effect check', {
      user_isAuthed: !!user?.isAuthenticated,
      sessionId_state: mask(sessionId),
      sessionId_storage: mask(sessionStorage.getItem('sessionId')),
      didCookieHydrate,
    });

    // If already authed, ensure flags are not stuck
    if (user?.isAuthenticated) {
      if (isLoading || !bootComplete) {
        logger.info('SessionIdAuth already authed -> force ready flags', { isLoading, bootComplete });
        setIsLoading(false);
        setBootComplete(true);
      }
      return;
    }

    if (!sessionId) {
      if (didCookieHydrate) {
        setIsLoading(false);
        setBootComplete(true);
      }
      return;
    }

    // prevent duplicate in-flight calls
    if (authInFlightRef.current) {
      logger.info('SessionIdAuth skipped (in-flight)', {});
      return;
    }
    authInFlightRef.current = true;

    const controller = new AbortController();
    let canceled = false;

    const applyRow = (row) => {
      const u = normalizeUserRow(row);

      const merged = {
        ...(safeParseJSON(sessionStorage.getItem('userInfo')) || {}),
        ...u,
        isAuthenticated: true,
        isMfaAuthenticated: null,
      };

      setUser(merged);
      sessionStorage.setItem('userInfo', JSON.stringify(merged));

      const acc = merged.iAccountID || merged.accountID;
      if (acc) {
        setIAccountID(acc);
        sessionStorage.setItem('iAccountID', acc);
      }

      const sid = u.session_id || row.session_id;
      if (sid) {
        sessionStorage.setItem('sessionId', sid);
        if (!sessionStorage.getItem('sessionCreationTime')) {
          sessionStorage.setItem('sessionCreationTime', new Date().toISOString());
        }
        setSessionId(sid);
      }
    };

    (async () => {
      setIsLoading(true);

      try {
        logger.info('SessionIdAuth calling /authenticateSession', { sessionId_state: mask(sessionId) });

        const { data } = await axios.get('/authenticateSession', {
          withCredentials: true,
          signal: controller.signal,
        });

        const row = Array.isArray(data) ? data[0] : null;
        if (!row) throw new Error('No auth row');
        applyRow(row);

        logger.info('SessionIdAuth OK', { rowSession_id: mask(row?.session_id) });
      } catch (err) {
        if (isCanceled(err)) {
          canceled = true;
          logger.info('SessionIdAuth canceled -> do not finalize boot state', {});
          return;
        }

        logger.info('SessionIdAuth FAILED -> cookie fallback', {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
        });

        // clear sessionId and try cookie-based auth once
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('sessionCreationTime');
        setSessionId('');

        try {
          const { data } = await axios.get('/authenticateSession', {
            withCredentials: true,
            signal: controller.signal,
          });

          const row = Array.isArray(data) ? data[0] : null;
          if (!row) {
            setUser(null);
            return;
          }
          applyRow(row);
          logger.info('SessionIdAuth fallback OK', { rowSession_id: mask(row?.session_id) });
        } catch (err2) {
          if (isCanceled(err2)) {
            canceled = true;
            logger.info('SessionIdAuth fallback canceled -> do not finalize boot state', {});
            return;
          }
          logger.info('SessionIdAuth fallback FAILED', {
            status: err2?.response?.status,
            data: err2?.response?.data,
            message: err2?.message,
          });
          setUser(null);
        }
      } finally {
        authInFlightRef.current = false;

        // ✅ If canceled due to cleanup/navigation, DON'T set bootComplete true with a null user
        if (canceled) return;

        setIsLoading(false);
        setBootComplete(true);
        logger.info('SessionIdAuth FINALLY', { bootComplete: true, isLoading: false });
      }
    })();

    return () => {
      controller.abort();
      authInFlightRef.current = false;
    };
    // ✅ CRITICAL FIX: DO NOT include isLoading/bootComplete here
  }, [sessionId, didCookieHydrate, user?.isAuthenticated]);

  /* 2) MFA session check — scoped to the current incident when present */
  useEffect(() => {
    if (!user?.isAuthenticated) return;

    const accountID = user.accountID || user.iAccountID || iAccountID;
    const userID = user.userID || user.iUserID;
    if (!accountID || !userID) return;

    const incidentID = getCurrentIncidentID();
    const sidNow = sessionStorage.getItem('sessionId') || sessionId || '';
    const keyBase = sidNow ? `sid:${sidNow}` : `au:${accountID}|${userID}`;
    const key = `${keyBase}|incident:${incidentID || 'none'}`;

    if (mfaCheckedKeyRef.current === key) return;
    mfaCheckedKeyRef.current = key;

    (async () => {
      try {
        logger.info('MFA check calling /authenticate-mfa-session', {
          accountID,
          userID,
          incidentID: incidentID || null,
        });

        const resp = await axios.get('/authenticate-mfa-session', {
          params: incidentID ? { incidentID } : undefined,
          headers: { iUserID: userID, iAccountID: accountID },
          withCredentials: true,
        });

        const verified = resp?.data?.mfaVerified;
        const mode = resp?.data?.mfaAuthenticationMode ||
          (verified === true ? 'trusted' : verified === false ? 'required' : 'pending');
        const emergencyIncidentID = String(
          resp?.data?.emergencyIncidentID || resp?.data?.incidentID || ''
        ).trim();

        logger.info('MFA check OK', {
          mfaVerified: verified,
          mode,
          emergencyIncidentID: emergencyIncidentID || null,
        });

        setUser((prev) => ({
          ...prev,
          isMfaAuthenticated:
            verified === true ? true : verified === false ? false : null,
          mfaAuthenticationMode: mode,
          mfaEmergencyIncidentID:
            mode === 'emergency-incident' ? emergencyIncidentID : null,
        }));
      } catch (err) {
        logger.info('MFA check FAILED (unknown)', {
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
        setUser((prev) => ({
          ...prev,
          isMfaAuthenticated: null,
          mfaAuthenticationMode: 'pending',
          mfaEmergencyIncidentID: null,
        }));
      }
    })();
  }, [
    user?.isAuthenticated,
    user?.accountID,
    user?.iAccountID,
    user?.userID,
    user?.iUserID,
    iAccountID,
    sessionId,
  ]);

  /* persist user + location fields */
  useEffect(() => {
    if (user?.isAuthenticated) {
      sessionStorage.setItem('userInfo', JSON.stringify(user));
    }

    localStorage.setItem('iBuildingID', iBuildingID ?? '');
    localStorage.setItem('buildingName', buildingName ?? '');
    localStorage.setItem('address', address ?? '');
    localStorage.setItem('city', city ?? '');
    localStorage.setItem('istate', iState ?? '');
    localStorage.setItem('zipCode', zipCode ?? '');
    localStorage.setItem('latitude', latitude ?? '');
    localStorage.setItem('longitude', longitude ?? '');
    localStorage.setItem('hazmat', hazmat ?? '');
    localStorage.setItem('accountName', accountName ?? '');
    localStorage.setItem('ipath', iPath ?? '');

    if (iAccountID) sessionStorage.setItem('iAccountID', iAccountID);
  }, [
    iBuildingID,
    buildingName,
    address,
    city,
    iState,
    zipCode,
    latitude,
    longitude,
    hazmat,
    accountName,
    iPath,
    iAccountID,
    user,
  ]);

  const handleLogout = () => {
    setUser(null);
    setSessionId('');
    sessionStorage.clear(); // do NOT touch localStorage
  };

  const isReady = !!(
    user?.isAuthenticated &&
    (iAccountID || user?.accountID || user?.iAccountID) &&
    user?.isMfaAuthenticated !== false
  );

  const ctx = {
    user,
    setUser,
    isLoading,
    bootComplete,
    isReady,
    sessionId,
    setSessionId,

    iAccountID,
    setIAccountID,
    iBuildingID,
    setIBuildingID,
    buildingName,
    setBuildingName,
    address,
    setAddress,
    city,
    setCity,
    iState,
    setIState,
    zipCode,
    setZipCode,
    latitude,
    setLatitude,
    longitude,
    setLongitude,
    hazmat,
    setHazmat,
    accountName,
    setAccountName,
    iPath,
    setIpath,

    isTestIncident,
    setIsTestIncident,

    handleLogout,
  };

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
}

 


// // ────────────────────────────────────────────────────────────────
// //  userAlertContext.js – central state provider for IncidentAlert
// //  (SchoolShield-aligned: /authenticateSession + /authenticate-mfa-session)
// // ────────────────────────────────────────────────────────────────
// import React, { createContext, useEffect, useRef, useState } from 'react';
// import axios, { logger } from './components/axiosAlertConfig';

// export { logger };
// export const UserContext = createContext(null);

// // Match the path you registered in IncidentAlert routes
// const MFA_ROUTE = '/mfaverification';

// // Where to land after MFA success
// const DEFAULT_AFTER_LOGIN_ROUTE = '/reportincidentpanel';

// function safeParseJSON(str) {
//   try { return JSON.parse(str); } catch { return null; }
// }

// const mask = (v) => {
//   const s = String(v ?? '');
//   if (!s) return '(none)';
//   if (s.length <= 10) return s;
//   return `${s.slice(0, 4)}…${s.slice(-4)}`;
// };

// const summarizeUser = (u) => {
//   if (!u) return null;
//   return {
//     isAuthenticated: u.isAuthenticated,
//     isMfaAuthenticated: u.isMfaAuthenticated,
//     accountID: u.accountID || u.iAccountID,
//     userID: u.userID || u.iUserID,
//     role: u.userRole,
//     hasSession_idField: !!u.session_id,
//   };
// };

// function normalizeUserRow(row = {}) {
//   const accountID =
//     row.accountID ??
//     row.iAccountID ??
//     row.iaccountid ??
//     row.iAccountId ??
//     '';

//   const userID =
//     row.userID ??
//     row.iUserID ??
//     row.iuserid ??
//     row.iUserId ??
//     '';

//   const firstName =
//     row.firstName ??
//     row.firstname ??
//     row.FirstName ??
//     row.userFirstName ??
//     row.UserFirstName ??
//     '';

//   const lastName =
//     row.lastName ??
//     row.lastname ??
//     row.LastName ??
//     row.userLastName ??
//     row.UserLastName ??
//     '';

//   const userMobileNumber =
//     row.userMobileNumber ??
//     row.UserMobileNumber ??
//     row.mobileNumber ??
//     row.MobileNumber ??
//     row.mobile ??
//     row.phone ??
//     row.Phone ??
//     '';

//   return {
//     ...row,
//     accountID: accountID || row.accountID || '',
//     iAccountID: accountID || row.iAccountID || '',
//     userID: userID || row.userID || '',
//     iUserID: userID || row.iUserID || '',
//     firstName,
//     lastName,
//     userMobileNumber,
//   };
// }

// const isCanceled = (err) =>
//   err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';

// export function UserProvider({ children }) {
//   /* ───────────── AUTH / SESSION ───────────── */

//   const [user, setUser] = useState(() => {
//     const cached = safeParseJSON(sessionStorage.getItem('userInfo'));
//     return cached ? { ...cached, isAuthenticated: false, isMfaAuthenticated: null } : null;
//   });

//   const [sessionId, setSessionId] = useState(() => sessionStorage.getItem('sessionId') || '');
//   const [isLoading, setIsLoading] = useState(true);
//   const [bootComplete, setBootComplete] = useState(false);
//   const [didCookieHydrate, setDidCookieHydrate] = useState(false);

//   const [iAccountID, setIAccountID] = useState(() =>
//     sessionStorage.getItem('iAccountID') ||
//     safeParseJSON(sessionStorage.getItem('userInfo'))?.iAccountID ||
//     safeParseJSON(sessionStorage.getItem('userInfo'))?.accountID ||
//     ''
//   );

//   const [isTestIncident, setIsTestIncident] = useState(() => {
//     if (localStorage.getItem('isTestIncidentOverride') === '1') return true;
//     return localStorage.getItem('isTestIncident') === '1';
//   });

//   /* ───────────── LOCATION (restored) ───────────── */
//   const [iBuildingID, setIBuildingID] = useState(localStorage.getItem('iBuildingID') || '');
//   const [buildingName, setBuildingName] = useState(localStorage.getItem('buildingName') || '');
//   const [address, setAddress] = useState(localStorage.getItem('address') || '');
//   const [city, setCity] = useState(localStorage.getItem('city') || '');
//   const [iState, setIState] = useState(localStorage.getItem('istate') || '');
//   const [zipCode, setZipCode] = useState(localStorage.getItem('zipCode') || '');
//   const [latitude, setLatitude] = useState(localStorage.getItem('latitude') || '');
//   const [longitude, setLongitude] = useState(localStorage.getItem('longitude') || '');
//   const [hazmat, setHazmat] = useState(localStorage.getItem('hazmat') || '');
//   const [accountName, setAccountName] = useState(localStorage.getItem('accountName') || '');
//   const [iPath, setIpath] = useState(localStorage.getItem('ipath') || '');

//   /* live ref so event handler doesn't use stale state */
//   const authedRef = useRef(false);
//   useEffect(() => { authedRef.current = !!user?.isAuthenticated; }, [user?.isAuthenticated]);

//   /* prevent duplicate SessionIdAuth calls */
//   const authInFlightRef = useRef(false);

//   /* MFA check: run once per session */
//   const mfaCheckedKeyRef = useRef('');

//   /* log initial boot snapshot once */
//   useEffect(() => {
//     logger.info('INIT state', {
//       sessionId_state: mask(sessionId),
//       sessionId_storage: mask(sessionStorage.getItem('sessionId')),
//       cachedUserInfo: summarizeUser(safeParseJSON(sessionStorage.getItem('userInfo'))),
//       didCookieHydrate,
//       bootComplete,
//       isLoading,
//     });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   /* listen for fresh sessionIds broadcast by LoginNew.jsx */
//   useEffect(() => {
//     const h = (e) => {
//       const sid = e.detail?.sessionId;
//       logger.info('EVENT newSessionId', { sid: mask(sid), raw: sid });

//       if (!sid) return;

//       sessionStorage.setItem('sessionId', sid);
//       setSessionId(sid);

//       if (!sessionStorage.getItem('sessionCreationTime')) {
//         sessionStorage.setItem('sessionCreationTime', new Date().toISOString());
//       }

//       // If cookie SSO already authenticated us, DO NOT force loading
//       const cached = safeParseJSON(sessionStorage.getItem('userInfo'));
//       const alreadyAuthed = !!(cached?.isAuthenticated || authedRef.current);

//       if (alreadyAuthed) {
//         setIsLoading(false);
//         setBootComplete(true);
//         logger.info('EVENT newSessionId while already authed -> keep UI ready', {});
//       } else {
//         setIsLoading(true);
//         setBootComplete(false);
//         logger.info('EVENT newSessionId -> start SessionIdAuth', {});
//       }
//     };

//     window.addEventListener('newSessionId', h);
//     return () => window.removeEventListener('newSessionId', h);
//   }, []);

//   /* persist isTestIncident */
//   useEffect(() => {
//     const v = isTestIncident ? '1' : '0';
//     sessionStorage.setItem('isTestIncident', v);
//     localStorage.setItem('isTestIncident', v);
//   }, [isTestIncident]);

//   /* ──────────────────────────────────────────────
//      0) Cookie bootstrap (SSO behavior)
//      ────────────────────────────────────────────── */
//   useEffect(() => {
//     if (didCookieHydrate) return;
//     if (user?.isAuthenticated) { setDidCookieHydrate(true); return; }
//     if (sessionId) { setDidCookieHydrate(true); return; } // sessionId flow will handle it

//     logger.info('CookieHydrate effect check', {
//       didCookieHydrate,
//       user_isAuthed: !!user?.isAuthenticated,
//       sessionId_state: mask(sessionId),
//       sessionId_storage: mask(sessionStorage.getItem('sessionId')),
//     });

//     (async () => {
//       setIsLoading(true);

//       try {
//         logger.info('CookieHydrate calling /authenticateSession', { withCredentials: true });

//         const { data } = await axios.get('/authenticateSession', { withCredentials: true });

//         const row = Array.isArray(data) ? data[0] : null;
//         if (!row) return;

//         const u = normalizeUserRow(row);

//         const sid = u.session_id || row.session_id;
//         if (sid) {
//           logger.info('CookieHydrate storing sessionId', { sid: mask(sid) });
//           sessionStorage.setItem('sessionId', sid);
//           if (!sessionStorage.getItem('sessionCreationTime')) {
//             sessionStorage.setItem('sessionCreationTime', new Date().toISOString());
//           }
//           setSessionId(sid);
//         }

//         const merged = {
//           ...(user || {}),
//           ...u,
//           isAuthenticated: true,
//           isMfaAuthenticated: null,
//         };

//         setUser(merged);
//         sessionStorage.setItem('userInfo', JSON.stringify(merged));
//         logger.info('CookieHydrate setUser merged', { merged: summarizeUser(merged) });

//         const acc = merged.iAccountID || merged.accountID;
//         if (acc) {
//           setIAccountID(acc);
//           sessionStorage.setItem('iAccountID', acc);
//         }
//       } catch (err) {
//         logger.info('CookieHydrate FAILED', {
//           message: err?.message,
//           status: err?.response?.status,
//           data: err?.response?.data,
//         });
//       } finally {
//         setDidCookieHydrate(true);
//         setIsLoading(false);
//         setBootComplete(true);
//         logger.info('CookieHydrate FINALLY', { didCookieHydrate: true, bootComplete: true, isLoading: false });
//       }
//     })();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [didCookieHydrate, sessionId, user?.isAuthenticated]);

//   /* ──────────────────────────────────────────────
//      1) Authenticate existing SESSIONID
//      ✅ FIX: dependencies DO NOT include isLoading/bootComplete
//      (prevents abort/cancel loop that kicked you to /login on refresh)
//      ────────────────────────────────────────────── */
//   useEffect(() => {
//     logger.info('SessionIdAuth effect check', {
//       user_isAuthed: !!user?.isAuthenticated,
//       sessionId_state: mask(sessionId),
//       sessionId_storage: mask(sessionStorage.getItem('sessionId')),
//       didCookieHydrate,
//     });

//     // If already authed, ensure flags are not stuck
//     if (user?.isAuthenticated) {
//       if (isLoading || !bootComplete) {
//         logger.info('SessionIdAuth already authed -> force ready flags', { isLoading, bootComplete });
//         setIsLoading(false);
//         setBootComplete(true);
//       }
//       return;
//     }

//     if (!sessionId) {
//       if (didCookieHydrate) {
//         setIsLoading(false);
//         setBootComplete(true);
//       }
//       return;
//     }

//     // prevent duplicate in-flight calls
//     if (authInFlightRef.current) {
//       logger.info('SessionIdAuth skipped (in-flight)', {});
//       return;
//     }
//     authInFlightRef.current = true;

//     const controller = new AbortController();
//     let canceled = false;

//     const applyRow = (row) => {
//       const u = normalizeUserRow(row);

//       const merged = {
//         ...(safeParseJSON(sessionStorage.getItem('userInfo')) || {}),
//         ...u,
//         isAuthenticated: true,
//         isMfaAuthenticated: null,
//       };

//       setUser(merged);
//       sessionStorage.setItem('userInfo', JSON.stringify(merged));

//       const acc = merged.iAccountID || merged.accountID;
//       if (acc) {
//         setIAccountID(acc);
//         sessionStorage.setItem('iAccountID', acc);
//       }

//       const sid = u.session_id || row.session_id;
//       if (sid) {
//         sessionStorage.setItem('sessionId', sid);
//         if (!sessionStorage.getItem('sessionCreationTime')) {
//           sessionStorage.setItem('sessionCreationTime', new Date().toISOString());
//         }
//         setSessionId(sid);
//       }
//     };

//     (async () => {
//       setIsLoading(true);

//       try {
//         logger.info('SessionIdAuth calling /authenticateSession', { sessionId_state: mask(sessionId) });

//         const { data } = await axios.get('/authenticateSession', {
//           withCredentials: true,
//           signal: controller.signal,
//         });

//         const row = Array.isArray(data) ? data[0] : null;
//         if (!row) throw new Error('No auth row');
//         applyRow(row);

//         logger.info('SessionIdAuth OK', { rowSession_id: mask(row?.session_id) });
//       } catch (err) {
//         if (isCanceled(err)) {
//           canceled = true;
//           logger.info('SessionIdAuth canceled -> do not finalize boot state', {});
//           return;
//         }

//         logger.info('SessionIdAuth FAILED -> cookie fallback', {
//           message: err?.message,
//           status: err?.response?.status,
//           data: err?.response?.data,
//         });

//         // clear sessionId and try cookie-based auth once
//         sessionStorage.removeItem('sessionId');
//         sessionStorage.removeItem('sessionCreationTime');
//         setSessionId('');

//         try {
//           const { data } = await axios.get('/authenticateSession', {
//             withCredentials: true,
//             signal: controller.signal,
//           });

//           const row = Array.isArray(data) ? data[0] : null;
//           if (!row) {
//             setUser(null);
//             return;
//           }
//           applyRow(row);
//           logger.info('SessionIdAuth fallback OK', { rowSession_id: mask(row?.session_id) });
//         } catch (err2) {
//           if (isCanceled(err2)) {
//             canceled = true;
//             logger.info('SessionIdAuth fallback canceled -> do not finalize boot state', {});
//             return;
//           }
//           logger.info('SessionIdAuth fallback FAILED', {
//             status: err2?.response?.status,
//             data: err2?.response?.data,
//             message: err2?.message,
//           });
//           setUser(null);
//         }
//       } finally {
//         authInFlightRef.current = false;

//         // ✅ If canceled due to cleanup/navigation, DON'T set bootComplete true with a null user
//         if (canceled) return;

//         setIsLoading(false);
//         setBootComplete(true);
//         logger.info('SessionIdAuth FINALLY', { bootComplete: true, isLoading: false });
//       }
//     })();

//     return () => {
//       controller.abort();
//       authInFlightRef.current = false;
//     };
//     // ✅ CRITICAL FIX: DO NOT include isLoading/bootComplete here
//   }, [sessionId, didCookieHydrate, user?.isAuthenticated]);

//   /* 2) MFA session check — run once per session */
//   useEffect(() => {
//     if (!user?.isAuthenticated) return;

//     const accountID = user.accountID || user.iAccountID || iAccountID;
//     const userID = user.userID || user.iUserID;
//     if (!accountID || !userID) return;

//     const sidNow = sessionStorage.getItem('sessionId') || sessionId || '';
//     const key = sidNow ? `sid:${sidNow}` : `au:${accountID}|${userID}`;

//     if (mfaCheckedKeyRef.current === key) return;
//     mfaCheckedKeyRef.current = key;

//     (async () => {
//       try {
//         logger.info('MFA check calling /authenticate-mfa-session', { accountID, userID });

//         const resp = await axios.get('/authenticate-mfa-session', {
//           headers: { iUserID: userID, iAccountID: accountID },
//           withCredentials: true,
//         });

//         const verified = resp?.data?.mfaVerified;
//         logger.info('MFA check OK', { mfaVerified: verified });

//         if (verified === true) {
//           setUser(prev => ({ ...prev, isMfaAuthenticated: true }));
//           if (window.location.pathname === MFA_ROUTE) {
//             window.location.replace(DEFAULT_AFTER_LOGIN_ROUTE);
//           }
//         } else if (verified === false) {
//           setUser(prev => ({ ...prev, isMfaAuthenticated: false }));
//           if (window.location.pathname !== MFA_ROUTE) {
//             window.location.replace(MFA_ROUTE);
//           }
//         } else {
//           setUser(prev => ({ ...prev, isMfaAuthenticated: null }));
//         }
//       } catch (err) {
//         logger.info('MFA check FAILED (unknown)', {
//           status: err?.response?.status,
//           data: err?.response?.data,
//           message: err?.message,
//         });
//         setUser(prev => ({ ...prev, isMfaAuthenticated: null }));
//       }
//     })();
//   }, [user?.isAuthenticated, user?.accountID, user?.iAccountID, user?.userID, user?.iUserID, iAccountID, sessionId]);

//   /* persist user + location fields */
//   useEffect(() => {
//     if (user?.isAuthenticated) {
//       sessionStorage.setItem('userInfo', JSON.stringify(user));
//     }

//     localStorage.setItem('iBuildingID', iBuildingID ?? '');
//     localStorage.setItem('buildingName', buildingName ?? '');
//     localStorage.setItem('address', address ?? '');
//     localStorage.setItem('city', city ?? '');
//     localStorage.setItem('istate', iState ?? '');
//     localStorage.setItem('zipCode', zipCode ?? '');
//     localStorage.setItem('latitude', latitude ?? '');
//     localStorage.setItem('longitude', longitude ?? '');
//     localStorage.setItem('hazmat', hazmat ?? '');
//     localStorage.setItem('accountName', accountName ?? '');
//     localStorage.setItem('ipath', iPath ?? '');

//     if (iAccountID) sessionStorage.setItem('iAccountID', iAccountID);
//   }, [
//     iBuildingID,
//     buildingName,
//     address,
//     city,
//     iState,
//     zipCode,
//     latitude,
//     longitude,
//     hazmat,
//     accountName,
//     iPath,
//     iAccountID,
//     user,
//   ]);

//   const handleLogout = () => {
//     setUser(null);
//     setSessionId('');
//     sessionStorage.clear(); // do NOT touch localStorage
//   };

//   const isReady = !!(
//     user?.isAuthenticated &&
//     (iAccountID || user?.accountID || user?.iAccountID) &&
//     user?.isMfaAuthenticated !== false
//   );

//   const ctx = {
//     user,
//     setUser,
//     isLoading,
//     bootComplete,
//     isReady,
//     sessionId,
//     setSessionId,

//     iAccountID,
//     setIAccountID,
//     iBuildingID,
//     setIBuildingID,
//     buildingName,
//     setBuildingName,
//     address,
//     setAddress,
//     city,
//     setCity,
//     iState,
//     setIState,
//     zipCode,
//     setZipCode,
//     latitude,
//     setLatitude,
//     longitude,
//     setLongitude,
//     hazmat,
//     setHazmat,
//     accountName,
//     setAccountName,
//     iPath,
//     setIpath,

//     isTestIncident,
//     setIsTestIncident,

//     handleLogout,
//   };

//   return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
// }

 
 